import {
  MessagePrimitive,
  type TextMessagePartComponent,
  useAuiState,
} from '@assistant-ui/react';
import {Icon} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';

import {ImageThumbnail} from './attachments/ImageThumbnail';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {decodePromptForMessageDisplay} from './utils';

const COLLAPSED_LINE_CLAMP_CLASS = 'line-clamp-6';

/**
 * useClampOverflow reports whether a line-clamped element is actually hiding
 * content, re-measuring on size changes (e.g., window resize) via a
 * ResizeObserver.
 */
const useClampOverflow = (expanded: boolean) => {
  const [overflowing, setOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (expanded) {
      return;
    }
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded]);

  return {contentRef, overflowing};
};

export const CollapsibleUserContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  const {contentRef, overflowing} = useClampOverflow(expanded);

  return (
    <>
      <div
        ref={contentRef}
        className={clsx(!expanded && COLLAPSED_LINE_CLAMP_CLASS)}>
        {children}
      </div>
      {(overflowing || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(prev => !prev)}
          className="mt-6 flex items-center gap-4 bg-transparent text-sm font-semibold text-moon-500 transition-colors hover:text-moon-700 dark:text-moon-450 dark:hover:text-moon-350 cursor-pointer">
          {expanded ? 'Show less' : 'Show more'}
          <span
            className={clsx(
              'inline-flex shrink-0 transition-[rotate] duration-[220ms]',
              expanded ? 'rotate-180' : 'rotate-0'
            )}>
            <Icon icon="chevron-down" size="sm" />
          </span>
        </button>
      )}
    </>
  );
};

/** UserText renders plain text parts inside the user message bubble.
 * `decodePromptForMessageDisplay` handles legacy threads created
 * pre-prompt-parts API, which carry encoded mention markers
 * (e.g., {`run_id: "...", run_display_name: "..."}`).
 */
const UserText: TextMessagePartComponent = ({text}) => {
  const cleaned = decodePromptForMessageDisplay(text);

  if (cleaned === '') {
    return null;
  }

  return <span>{cleaned}</span>;
};

// Images are rendered as thumbnails above the chat bubble (see below), so suppress
// the default inline image rendering inside the bubble's part list.
const NoInlineImage = () => null;

export const UserMessage = () => {
  const nightModeRevertFilter = useNightModeRevertFilter();
  const parts = useAuiState(s => s.message.parts);

  type MessagePart = (typeof parts)[number];
  const imageParts = useMemo(
    () =>
      parts.filter(
        (p): p is Extract<MessagePart, {type: 'image'}> => p.type === 'image'
      ),
    [parts]
  );

  // Hide the chat bubble entirely for image-only messages
  const hasBubbleContent = useMemo(
    () =>
      parts.some(p =>
        p.type === 'text' ? p.text.trim().length > 0 : p.type !== 'image'
      ),
    [parts]
  );

  return (
    <MessagePrimitive.Root
      className="flex flex-col items-end gap-10 pl-24 pt-16"
      data-role="user">
      {imageParts.length > 0 && (
        <div className="flex max-w-full gap-8 overflow-x-auto [scrollbar-width:thin]">
          {imageParts.map((part, index) => (
            // A sent message's image list is immutable (never reordered or
            // mutated), and these parts carry no stable id, so an index key is
            // safe here.
            // oxlint-disable-next-line react/no-array-index-key
            <ImageThumbnail key={index} src={part.image} />
          ))}
        </div>
      )}
      {hasBubbleContent && (
        <div className="relative max-w-full break-words overflow-hidden rounded-2xl px-16 py-8 text-moon-800 text-[15px] font-normal">
          <div
            className="absolute inset-0 rounded-2xl bg-moon-100 dark:bg-moon-750"
            style={{filter: nightModeRevertFilter}}
          />
          <div className="relative whitespace-pre-wrap [&_[data-component=Pill]]:mx-2 [&_[data-component=Pill]]:align-middle">
            <CollapsibleUserContent>
              <MessagePrimitive.Parts>
                {({part}) => {
                  switch (part.type) {
                    case 'text':
                      return <UserText {...part} />;
                    case 'image':
                      return <NoInlineImage />;
                    // Mentions resolve to their per-type renderer through the
                    // data registry; unregistered types render nothing.
                    case 'data':
                      return part.dataRendererUI ?? null;
                    default:
                      return null;
                  }
                }}
              </MessagePrimitive.Parts>
            </CollapsibleUserContent>
          </div>
        </div>
      )}
    </MessagePrimitive.Root>
  );
};
